import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

export const AboutUsSection = () => {
  const sectionRef = useRef(null);
  const labelRef = useRef(null);
  const titleRef = useRef(null);
  const paragraphsRef = useRef([]);
  const quoteRef = useRef(null);
  const ctaRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Label
      gsap.fromTo(labelRef.current,
        { y: -20, opacity: 0, scale: 0.8 },
        {
          y: 0, opacity: 1, scale: 1, duration: 0.6, ease: 'back.out(1.7)',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 80%', toggleActions: 'play none none reverse' },
        }
      );

      // Title
      gsap.fromTo(titleRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 75%', toggleActions: 'play none none reverse' },
        }
      );

      // Paragraphs
      paragraphsRef.current.forEach((p, i) => {
        if (!p) return;
        gsap.fromTo(p,
          { y: 30, opacity: 0 },
          {
            y: 0, opacity: 1, duration: 0.7, ease: 'power2.out', delay: i * 0.12,
            scrollTrigger: { trigger: p, start: 'top 88%', toggleActions: 'play none none reverse' },
          }
        );
      });

      // Quote
      if (quoteRef.current) {
        gsap.fromTo(quoteRef.current,
          { x: -50, opacity: 0 },
          {
            x: 0, opacity: 1, duration: 0.9, ease: 'power3.out',
            scrollTrigger: { trigger: quoteRef.current, start: 'top 85%', toggleActions: 'play none none reverse' },
          }
        );
      }

      // Image
      if (imageRef.current) {
        gsap.fromTo(imageRef.current,
          { x: 60, opacity: 0, scale: 0.95 },
          {
            x: 0, opacity: 1, scale: 1, duration: 1, ease: 'power3.out',
            scrollTrigger: { trigger: imageRef.current, start: 'top 80%', toggleActions: 'play none none reverse' },
          }
        );
      }

      // CTA
      if (ctaRef.current) {
        gsap.fromTo(ctaRef.current,
          { y: 25, opacity: 0 },
          {
            y: 0, opacity: 1, duration: 0.7, ease: 'power2.out',
            scrollTrigger: { trigger: ctaRef.current, start: 'top 92%', toggleActions: 'play none none reverse' },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="about_us_section"
      className="w-full bg-[#fdfdfd] py-12 sm:py-16 relative overflow-hidden"
    >
      {/* Decorative blurred blobs */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-pumpkinToast-300/20 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#2a1273]/10 rounded-full blur-[150px]" />

      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 xl:px-10 relative z-10">
        {/* Label */}
        <div ref={labelRef} className="mb-8 text-center opacity-0">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#2a1273] bg-[#2a1273]/8 rounded-full border border-[#2a1273]/15">
            About Us
          </span>
        </div>

        {/* Title */}
        <h2
          ref={titleRef}
          className="text-[#2a1273] text-center mb-6 opacity-0"
        >
          <span className="block font-cairo font-bold text-3xl sm:text-4xl lg:text-5xl mb-2 text-pumpkinToast-500">
            دار الحديث
          </span>
          <span className="block text-lg sm:text-xl lg:text-2xl font-normal text-gray-600 font-libre">
            Islamic Research Center
          </span>
        </h2>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mt-12 items-center">
          {/* Left - text */}
          <div className="space-y-6 text-left">
            <p
              ref={el => (paragraphsRef.current[0] = el)}
              className="text-gray-700 text-base sm:text-xl leading-relaxed text-justify hyphens-auto opacity-0"
            >
              All the teachings and positions of Islam in terms of faith, customs, and practices are based on divine revelations. What distinguishes Islam from other philosophies and ideologies is its pure principles, which are not subject to human interference.
            </p>

            <p
              ref={el => (paragraphsRef.current[1] = el)}
              className="text-gray-700 text-base sm:text-xl leading-relaxed text-justify hyphens-auto opacity-0"
            >
              Hadiths are of paramount importance in Islamic scriptures. The Sunnah of the Holy Prophet (ﷺ) has an integral place in analyzing and implementing the beginning and end of human life.
            </p>

            {/* Quote block */}
            <div
              ref={quoteRef}
              className="relative pl-6 py-4 opacity-0"
            >
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-pumpkinToast-500 to-[#2a1273] rounded-full" />
              <p className="text-gray-800 italic text-[1.05rem] sm:text-lg leading-relaxed font-medium">
                A center for systematic hadith study — that is the Darul Hadith Islamic Research Center.              </p>
            </div>

            {/* CTA */}
            <div ref={ctaRef} className="flex gap-4 flex-wrap pt-2 opacity-0">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm sm:text-base font-semibold text-white bg-pumpkinToast-500 rounded-lg hover:bg-pumpkinToast-400 transition-all duration-300 hover:-translate-y-0.5 shadow-md shadow-pumpkinToast-500/25"
              >
                Read More
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm sm:text-base font-semibold text-[#2a1273] border-2 border-[#2a1273]/20 rounded-lg hover:bg-[#2a1273]/5 transition-all duration-300 hover:-translate-y-0.5"
              >
                Contact Us
              </Link>
            </div>
          </div>

          {/* Right - image */}
          <div ref={imageRef} className="relative opacity-0">
            <div className="absolute -inset-3 bg-gradient-to-br from-pumpkinToast-200 to-[#2a1273]/20 rounded-2xl transform rotate-2 z-0" />
            <img
              src="/assets/building_9_16.jpeg"
              alt="Darul Hadees Campus"
              className="relative rounded-2xl shadow-2xl w-full h-[350px] sm:h-[420px] object-cover z-10"
              loading="lazy"
            />
            {/* Floating accent card */}
            {/* <div className="absolute -bottom-6 -left-4 sm:-left-6 bg-white rounded-xl shadow-xl px-5 py-4 z-20 border border-gray-100">
              <p className="text-2xl sm:text-3xl font-bold text-pumpkinToast-500 font-AndoraModernSerif">25+</p>
              <p className="text-xs sm:text-sm text-gray-500 font-medium">Years of Excellence</p>
            </div> */}
          </div>
        </div>
      </div>
    </section>
  );
};
